import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import Dropdown from '../../../../components/Dropdown';
import { useConsultation } from '../../../../context/ConsultationContext';
import styles from '../TreatmentTypes/css/BloodPressure.module.css';
import newStyle from './css/DiscoveryVitality.module.css';
import { ClipboardPlus, Send } from 'lucide-react';

const MomentumHRA = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { consultationData, updateConsultationData, getStepDraft, setStepDraft } = useConsultation();

    const patient = location?.state?.patient || consultationData?.patient || {};
    const patientName = `${patient?.firstname || ""} ${patient?.surname || ""}`.trim();
    const profileNumber = patient?.profilenumber;
    const initials = `${patient?.firstname?.[0] || ""}${patient?.surname?.[0] || ""}`.toUpperCase();

    // Lifestyle factors states
    const [smokingStatus, setSmokingStatus] = useState('');
    const [alcoholIntake, setAlcoholIntake] = useState('');
    const [exerciseFrequency, setExerciseFrequency] = useState('');
    
    // Body measurements states
    const [weight, setWeight] = useState('');
    const [height, setHeight] = useState('');
    const [bmi, setBmi] = useState('');
    
    // Status states
    const [submissionStatus, setSubmissionStatus] = useState('');
    
    // Other states
    const [clinicalNotes, setClinicalNotes] = useState('');

    // Calculate BMI when weight or height changes
    useEffect(() => {
        if (weight && height) {
            const weightInKg = parseFloat(weight);
            const heightInM = parseFloat(height) / 100; // Convert cm to m
            const calculatedBmi = (weightInKg / (heightInM * heightInM)).toFixed(1);
            setBmi(calculatedBmi);
        } else {
            setBmi('');
        }
    }, [weight, height]);

    const handleInvoice = () => {
        // Persist captured values as draft data (optional, for future reporting)
        const st = location?.state || {};
        const workflowServiceId =
            consultationData?.activeWorkflowServiceId ||
            st.addedServiceId ||
            st.serviceId ||
            st.serviceid ||
            consultationData?.serviceId;

        const existing = getStepDraft?.('serviceWorkflowData', {}) || {};
        const existingForService = (workflowServiceId && existing?.[workflowServiceId]) ? existing[workflowServiceId] : {};
        setStepDraft?.('serviceWorkflowData', {
            ...existing,
            ...(workflowServiceId ? {
                [workflowServiceId]: {
                    ...(existingForService || {}),
                    momentum_hra: {
                        smokingStatus,
                        alcoholIntake,
                        exerciseFrequency,
                        weight,
                        height,
                        bmi,
                        submissionStatus,
                        clinicalNotes,
                    },
                },
            } : {}),
        });

        // If identifiers were passed via navigation state, store them into consultation context
        const nextData = {};
        const patientId = st.patientId ?? st.patientid;
        const appointmentId = st.appointmentId ?? st.appointmentid;
        const serviceId = st.serviceId ?? st.serviceid;

        if (patientId && !consultationData?.patientId) nextData.patientId = patientId;
        if (appointmentId && !consultationData?.appointmentId) nextData.appointmentId = appointmentId;
        if (serviceId && !consultationData?.serviceId) nextData.serviceId = serviceId;

        if (Object.keys(nextData).length) updateConsultationData?.(nextData);

        navigate('/customer/clinic/invoice', {
            state: {
                ...st,
                returnedFromWorkflow: true,
            },
        });
    };

    useEffect(() => {
        document.body.classList.add(styles.hideSidebar);
        return () => {
            document.body.classList.remove(styles.hideSidebar);
        };
    }, []);

    return (
        <div className={styles.bloodPressureContainer}>
            <Navbar />

            <main className={styles.mainContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div className={styles.headerIcon} aria-hidden="true">
                            <ClipboardPlus />
                        </div>
                        <h1 className={newStyle.title}>Health Risk Assessment</h1>
                    </div>

                    <div className={styles.patientCard}>
                        <div className={styles.avatar} aria-hidden="true">{initials || 'TN'}</div>
                        <div className={styles.patientText}>
                            <div className={styles.patientName}>{patientName || 'Thandiwe Ndlovu'}</div>
                            <div className={styles.patientMetaRow}>
                                <span className={styles.profileInfo}>Profile: {profileNumber || '448896'}</span>
                                <span className={styles.dependentBadge}>Dependent</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>LIFESTYLE FACTORS</div>
                        <div className={newStyle.readingsRow1}>
                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Smoking Status</label>
                                <div className={styles.inputGroup}>
                                    <Dropdown
                                        name="smokingStatus"
                                        value={smokingStatus}
                                        onChange={setSmokingStatus}
                                        options={['Non-smoker', 'Former smoker', 'Current smoker', 'Occasional smoker']}
                                        placeholder="Select..."
                                        className={newStyle.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Alcohol Intake</label>
                                <div className={styles.inputGroup}>
                                    <Dropdown
                                        name="alcoholIntake"
                                        value={alcoholIntake}
                                        onChange={setAlcoholIntake}
                                        options={['None', 'Occasional', 'Moderate', 'Heavy']}
                                        placeholder="Select..."
                                        className={newStyle.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Exercise Frequency</label>
                                <div className={styles.inputGroup}>
                                    <Dropdown
                                        name="exerciseFrequency"
                                        value={exerciseFrequency}
                                        onChange={setExerciseFrequency}
                                        options={['None', '1-2 times/week', '3-4 times/week', '5+ times/week']}
                                        placeholder="Select..."
                                        className={newStyle.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>BODY MEASUREMENTS</div>
                        <div className={newStyle.readingsRow}>
                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Weight</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={weight}
                                        onChange={(e) => setWeight(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={styles.unit}>kg</span>
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Height</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={height}
                                        onChange={(e) => setHeight(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                    />
                                    <span className={styles.unit}>cm</span>
                                </div>
                            </div>

                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>BMI (auto-calculated)</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        value={bmi}
                                        onChange={(e) => setBmi(e.target.value)}
                                        className={styles.readingInput}
                                        placeholder='—'
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>STATUS</div>
                        <div className={newStyle.readingsRow2}>
                            <div className={styles.readingItem}>
                                <label className={styles.readingLabel}>Submission to medical aid</label>
                                <div className={styles.inputGroup}>
                                    <Dropdown
                                        name="submissionStatus"
                                        value={submissionStatus}
                                        onChange={setSubmissionStatus}
                                        options={['Submitted', 'Pending', 'Rejected', 'Approved']}
                                        placeholder="Select..."
                                        className={newStyle.readingInput}
                                        alignLeft
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.sectionBlock}>
                        <div className={styles.fieldLabel}>Clinical Notes</div>
                        <textarea
                            value={clinicalNotes}
                            onChange={(e) => setClinicalNotes(e.target.value)}
                            className={styles.clinicalNotes}
                            placeholder="Enter clinical notes..."
                            rows={6}
                        />
                    </div>

                    <div className={styles.cardFooter}>
                        <Button
                            onClick={handleInvoice}
                            className={styles.invoiceButton}
                            type="button"
                        >
                            <Send size={16} />
                            Invoice
                        </Button>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default MomentumHRA;